import { useEffect, useState, type RefObject } from 'react';
import { useForm, useWatch, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Form, LoadingSpinner, QueryErrorAlert } from '@shared/components';
import {
    accessPromptSchema,
    type AccessPromptFormInput,
    type AccessPromptFormValues,
} from '../../schemas/accessPrompt.schema';
import type { CallerConfirmationDetails } from '../../types/agreementAccess.types';
import { allDetailsConfirmed, type ConfirmationSubmitError } from '../../utils/callerConfirmation';
import { AccessJustificationFields } from './AccessJustificationFields';
import { AuthorisedContactsList } from './AuthorisedContactsList';
import { CallerDetailsFields } from './CallerDetailsFields';
import { SECTION_DIVIDER_CLASSES, SECTION_HEADING_CLASSES } from './sectionStyles';

interface Props {
    headingId: string;
    headingRef: RefObject<HTMLHeadingElement | null>;
    isExpired: boolean;
    details: CallerConfirmationDetails | undefined;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    /** Resolves to null once an outcome is recorded, or to the errors to show against the form. */
    onSubmit: (values: AccessPromptFormValues) => Promise<ConfirmationSubmitError | null>;
}

const EMPTY_FORM: DefaultValues<AccessPromptFormInput> = {
    caseReference: '',
    reason: '',
    nameConfirmed: false,
    addressConfirmed: false,
    postcodeConfirmed: false,
};

const EXPIRED_NOTICE =
    "Your access has expired. Confirm the customer's details again to continue. Anything you have typed on this page is kept.";
const INCOMPLETE_NOTICE =
    'The Agreement opens only when all three details are ticked. Submitting now records the attempt without opening it.';

/** The one screen an internal user completes before the Agreement opens. */
export function AccessPrompt({ headingId, headingRef, isExpired, details, isLoading, isError, onRetry, onSubmit }: Props) {
    // After an expiry the shared Modal lands focus; focusing here first would become what it restores on close.
    useEffect(() => {
        if (!isExpired) headingRef.current?.focus();
    }, [headingRef, isExpired]);

    // The Try again button unmounts while the read is in flight, so focus moves to the heading first.
    const handleRetry = () => {
        headingRef.current?.focus();
        onRetry();
    };

    return (
        <div>
            <h2 id={headingId} ref={headingRef} tabIndex={-1} className="mb-3 text-lg font-semibold outline-none">
                Agreement access confirmation
            </h2>
            {details && !isError && !isLoading && (
                <div className="rounded-md border border-mp-border bg-mp-background px-4 py-3">
                    <p className="text-xs font-medium text-mp-muted">Agreement reference</p>
                    <p className="font-mono text-xl font-semibold text-mp-foreground">
                        {details.agreementRef ?? 'Not available'}
                    </p>
                </div>
            )}
            {isExpired && <p className="mt-2 text-sm text-mp-muted">{EXPIRED_NOTICE}</p>}
            {isError ? (
                <QueryErrorAlert
                    message="The customer's details could not be loaded."
                    onRetry={handleRetry}
                    className="mt-4"
                />
            ) : isLoading || !details ? (
                <LoadingSpinner message="Loading the customer's details" className="p-4" />
            ) : (
                <PromptFields headingId={headingId} details={details} onSubmit={onSubmit} />
            )}
        </div>
    );
}

type FieldsProps = Pick<Props, 'headingId' | 'onSubmit'> & { details: CallerConfirmationDetails };

function PromptFields({ headingId, details, onSubmit }: FieldsProps) {
    const [formError, setFormError] = useState<string | null>(null);
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<AccessPromptFormInput, unknown, AccessPromptFormValues>({
        resolver: zodResolver(accessPromptSchema),
        mode: 'onBlur',
        defaultValues: EMPTY_FORM,
    });
    const isComplete = allDetailsConfirmed(useWatch({ control }));

    const handleValid = async (values: AccessPromptFormValues) => {
        setFormError(null);
        const failure = await onSubmit(values);
        if (!failure) return;

        failure.fieldErrors.forEach(({ field, message }) => setError(field, { message }));
        setFormError(failure.message);
    };

    return (
        <Form onSubmit={handleSubmit(handleValid)} noValidate className="mt-6 space-y-6">
            {formError && <Alert variant="error">{formError}</Alert>}
            <section aria-labelledby={`${headingId}-reason-heading`} className="space-y-4">
                <h3 id={`${headingId}-reason-heading`} className={SECTION_HEADING_CLASSES}>
                    Why you need access
                </h3>
                <AccessJustificationFields idPrefix={headingId} control={control} errors={errors} disabled={isSubmitting} />
            </section>
            <div className={SECTION_DIVIDER_CLASSES}>
                <CallerDetailsFields
                    idPrefix={headingId}
                    details={details}
                    control={control}
                    errors={errors}
                    disabled={isSubmitting}
                />
            </div>
            <AuthorisedContactsList contacts={details.authorisedContacts} />
            <p role="status" className="text-sm text-mp-warning">
                {isComplete ? '' : INCOMPLETE_NOTICE}
            </p>
            <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Recording…' : 'Record and continue'}
                </Button>
            </div>
        </Form>
    );
}
